import React from 'react';
import { Link } from 'react-router-dom';
import { buildGalleryPath } from '../utils/slug';
import { buildGalleryResponsiveSrcSet, getGalleryDisplayImageUrl } from '../utils/images';
import { Gallery as GalleryType, Image } from '../types';

export type HomepageFeaturedImage = Image & { galleryId: string; galleryTitle: string };

type GalleryProps = {
    galleries: GalleryType[];
};

const getCollectionDescription = (title: string) => {
    const lower = title.toLowerCase();

    if (lower.includes('light')) {
        return 'Studies in contrast, shadow and soft tonal transitions.';
    }

    if (lower.includes('urban') || lower.includes('city') || lower.includes('street')) {
        return 'Architectural rhythm and quiet movement in modern spaces.';
    }

    if (lower.includes('botanical') || lower.includes('flora') || lower.includes('nature')) {
        return 'Organic textures and delicate forms framed with precision.';
    }

    if (lower.includes('polaroid') || lower.includes('analog') || lower.includes('film')) {
        return 'Tactile grain and nostalgic tones with contemporary composition.';
    }

    return 'Curated photographs shaped around atmosphere, texture and mood.';
};

const Gallery: React.FC<GalleryProps> = ({ galleries }) => {
    const selectedGalleries = galleries.slice(0, 4);

    return (
        <section className="home-showcase" id="home-curated">
            <header className="home-showcase__header">
                <div className="home-showcase__intro">
                    <p className="home-showcase__eyebrow">Collections</p>
                </div>
                <Link className="home-showcase__link" to="/galleries">
                    All
                </Link>
            </header>

            <div className="home-showcase__cards">
                {selectedGalleries.map((gallery, index) => {
                    const coverImage = gallery.images?.[0];
                    const coverImageUrl = getGalleryDisplayImageUrl(coverImage);
                    return (
                        <article key={gallery.id} className="gallery-card">
                            <Link to={buildGalleryPath(gallery)} className="gallery-card__media">
                                <span className="gallery-card__index">0{index + 1}</span>
                                {coverImage ? (
                                    <img
                                        src={coverImageUrl}
                                        srcSet={buildGalleryResponsiveSrcSet(coverImage)}
                                        sizes="(max-width: 768px) 92vw, (max-width: 1200px) 44vw, 30vw"
                                        alt={coverImage.title || gallery.title}
                                        loading="lazy"
                                        decoding="async"
                                    />
                                ) : (
                                    <div className="gallery-card__placeholder" aria-hidden="true" />
                                )}
                                <div className="gallery-card__content">
                                    <h3>{gallery.title}</h3>
                                    <p>{getCollectionDescription(gallery.title)}</p>
                                    <span className="gallery-card__link">View Collection</span>
                                </div>
                            </Link>
                        </article>
                    );
                })}
            </div>
        </section>
    );
};

export default Gallery;